import React, {
	memo,
	useCallback,
	useState
} from 'react';
import PropTypes from 'prop-types';

import {
	handleError,
	log
} from '../../../services/utils';

import useForm from '../../../hooks/useForm';
import useToggle from '../../../hooks/useToggle';

import { getPasswordResetCode } from '../../../api/nora/auth';

import { useUIAction } from '../../../contexts/UIContext';

import AuthInput from '../AuthInput';
import SubmitButton from '../SubmitButton';
import TextButton from '../../Shared/TextButton';
import Alert from '../../Alert';

import styles from '../auth.module.css';

const ForgotPassForm = ( { toggleForgotPass } ) => {
	const { setScreenLoader } = useUIAction();

	const {
		formData,
		updateField
	} = useForm( { userName: '' } );

	const [ successModal, toggleSuccessModal ] = useToggle( false );
	const [ successMessage, setSuccessMessage ] = useState( '' );
	const [ errorModal, toggleErrorModal ] = useToggle( false );
	const [ errorModalMessage, setErrorModalMessage ] = useState( '' );

	const handleSubmit = useCallback( async () => {
		setScreenLoader( 'Processing Request...' );

		try {
			const response = await getPasswordResetCode( formData );

			if ( response.status === 200 ) {
				setSuccessMessage( <span>An email has been sent to the email on file for <strong>{ formData.userName }</strong> with further instructions. Please make sure to check your spam or junk folder if you are having trouble finding the email. If you still are unable to locate the email, please confirm that you are checking the email you provided us with when creating your NORA account. Please contact support for any further assistance.</span> );
				toggleSuccessModal();
			}

			setScreenLoader( '' );
		} catch ( error ) {
			setScreenLoader( '' );
			const errorMessage = handleError( error );

			setErrorModalMessage( errorMessage );
			toggleErrorModal();
		}
	}, [
		formData,
		setScreenLoader,
		toggleErrorModal,
		toggleSuccessModal
	] );

	const handleKeyDown = useCallback( e => {
		if ( e.key === 'Enter' ) {
			handleSubmit();
		}
	}, [ handleSubmit ] );

	const closeSuccessModal = useCallback( () => {
		toggleSuccessModal();
		toggleForgotPass();
	}, [
		toggleForgotPass,
		toggleSuccessModal
	] );

	return (
		<>
			{ log( 'forgotPassForm' ) }
			<div className={ styles.inputs }>
				<AuthInput
					icon="user"
					name="userName"
					onChange={ updateField }
					onKeyDown={ handleKeyDown }
					placeholder="Username"
					value={ formData.userName }
				/>
			</div>
			<SubmitButton
				buttonText="Request Password Reset Link"
				onSubmit={ handleSubmit }
			/>
			<TextButton
				buttonText="Cancel"
				className={ styles.textButton }
				onClick={ toggleForgotPass }
			/>
			<Alert
				name="forgot-pass-success-alert"
				heading="Password Reset Link Sent"
				message={ successMessage }
				open={ successModal }
				closeModal={ closeSuccessModal }
				closeText="Continue"
				type="success"
			/>
			<Alert
				name="forgot-pass-error-alert"
				heading="Unable to Process Request"
				message={ errorModalMessage }
				open={ errorModal }
				closeModal={ toggleErrorModal }
				type="error"
			/>
		</>
	);
};

ForgotPassForm.propTypes = { toggleForgotPass: PropTypes.func.isRequired };

export default memo( ForgotPassForm );
